import { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { groupBy } from 'lodash';
import { Moment } from 'jalali-moment';
import { Alert, App, Col, Row } from 'antd';
import { useModal } from '@/hooks';
import { useSelfContext } from '@/context';
import { DateSelectorBar, Flex, LoadingSpin } from '../../common';
import ReserveCard from './ReserveCard';

export interface ReserveProgramMobileProps {
  isNewReserveMode?: boolean;
  variant: AppModules;
  programs: SelfMealEntity[];
  isProgramsFetching: boolean;
  detailModal: ReturnType<typeof useModal>;
  changeSelfModal: ReturnType<typeof useModal>;
  date: Moment;
  dates: Moment[];
  nextWeek: () => void;
  prevWeek: () => void;
  locked: boolean;
  setLocked: (value: boolean) => any;
}

export default function ReserveProgramMobile({
  isNewReserveMode = false,
  variant,
  programs,
  isProgramsFetching,
  detailModal,
  changeSelfModal,
  date,
  dates,
  nextWeek,
  prevWeek,
  locked,
  setLocked
}: ReserveProgramMobileProps) {

  const { message: messageAPI } = App.useApp();
  const { selfText } = useSelfContext();
  const [selected, setSelected] = useState(0);

  const currentDate = dates[selected];
  const groupedPrograms = groupBy(programs, 'date');
  const meals = groupedPrograms[currentDate.clone().locale('en').format('YYYY-MM-DD')];
  const firstCallRef = useRef<boolean>(false);

  useEffect(() => {
    if (firstCallRef.current || Object.keys(groupedPrograms).length == 0) return;
    const index = findFirstReservableIndex(groupedPrograms);
    firstCallRef.current = true;
    setSelected(index);
  }, [firstCallRef, groupedPrograms]);

  return (
    <Row className="reserve-programs-container reserve-programs-container-mobile overflow-y-auto">
      <Col className="ps-4" span={7}>
        <DateSelectorBar
          dates={dates}
          variant={variant}
          selected={selected}
          startDate={date}
          onDayClick={setSelected}
          onPrevWeekClick={prevWeek}
          onNextWeekClick={nextWeek}
        />
      </Col>
      <Col span={17} className="!max-h-full !overflow-hidden">
        <Flex
          direction="column"
          align="center"
          className="mx-6 pb-2 text-sm font-bold text-[#777] mt-3"
          style={{ borderBottom: '1px solid #ddd' }}>
          {currentDate.format('jDD jMMM jYYYY')}
        </Flex>
        <Flex direction="column" className="reserve-programs-content reserve-programs-content-mobile">
          {isProgramsFetching ? <Flex className="h-full w-full"><LoadingSpin /></Flex> :
            meals == null || meals?.length === 0 ? (
              <Alert type="warning" className="flex w-full mt-2 !text-sm" message={selfText('empty-items')} />
            ) :
              meals?.map((meal, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 5 }}
                  className="flex flex-col mb-1"
                >
                  {meal.programFoodTypes.map((reserve) => (
                    reserve.foodList.map((_subFood) => {
                      const key = crypto.randomUUID();
                      return (
                        <ReserveCard
                          isNewReserveMode={isNewReserveMode}
                          locked={locked}
                          setLocked={setLocked}
                          key={key}
                          meal={meal}
                          reserve={reserve}
                          messageAPI={messageAPI}
                          onDetailClick={() => detailModal.show({ reserve, meal })}
                          onChangeSelf={() => changeSelfModal.show({ reserve, meal })}
                        />
                      );
                    })
                  ))}
                </motion.div>
              ))
          }
        </Flex>
      </Col>
    </Row >
  );
}

function findFirstReservableIndex(groupedPrograms: Dictionary<SelfMealEntity[]>) {
  let index: number;
  const keys = Object.keys(groupedPrograms);
  let found = false;
  for (index = 0; index < keys.length; index++) {
    const key = keys[index];
    const dayProgram = groupedPrograms[key];
    for (const programItem of dayProgram) {
      if (programItem.reserveRuleViolated || programItem?.programFoodTypes?.length == 0) {
        continue;
      }
      found = true;
      break;
    }
    if (found) break;
  }
  if (index >= keys.length) {
    index = 0;
  }
  return index;
}
